import { Component } from '@theme/component';

/**
 * Wraps an autoplaying <video> element and exposes a keyboard-accessible
 * pause/play toggle to comply with WCAG 2.2.2 (Pause, Stop, Hide).
 *
 * Expects a single <video> descendant as the playback target. Looks up the
 * toggle button via the `toggleButton` ref. Reads pause/play aria-labels from
 * the button's data-label-pause / data-label-play attributes.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} toggleButton
 *
 * @extends Component<Refs>
 */
export class PausableVideoComponent extends Component {
  requiredRefs = ['toggleButton'];

  /** @type {HTMLVideoElement | null} */
  #video = null;

  connectedCallback() {
    super.connectedCallback();

    this.#video = this.querySelector('video');
    if (!this.#video) return;

    this.#syncToggleState();
    this.#video.addEventListener('play', () => this.#syncToggleState());
    this.#video.addEventListener('pause', () => this.#syncToggleState());
  }

  togglePlayback() {
    if (!this.#video) return;
    if (this.#video.paused) {
      this.#video.play();
    } else {
      this.#video.pause();
    }
  }

  #syncToggleState() {
    const { toggleButton } = this.refs;
    if (!toggleButton || !this.#video) return;

    const isPaused = this.#video.paused;
    toggleButton.dataset.state = isPaused ? 'paused' : 'playing';
    const label = isPaused ? toggleButton.dataset.labelPlay : toggleButton.dataset.labelPause;
    if (label) toggleButton.setAttribute('aria-label', label);
  }
}

if (!customElements.get('pausable-video')) {
  customElements.define('pausable-video', PausableVideoComponent);
}
